import React from "react";
import { useTranslation } from "react-i18next";
import {
    DragDropContext,
    Droppable,
    Draggable,
    DropResult,
} from "react-beautiful-dnd";
import { AdItem } from "./types";
import {
    Card,
    SectionTitle,
    ImageSection,
    ImageHeader,
    Select,
    AddButton,
    TableWrapper,
    Table,
    Th,
    Td,
    DragHandle,
    OrderInput,
    ImageThumbnail,
    ActionsCell,
    SmallToggle,
    ViewButton,
    DeleteButton,
} from "./styled";

type AdsTableProps = {
    mainColor: string;
    displayType: string;
    onDisplayTypeChange: (value: string) => void;
    currentAds: AdItem[];
    fileInputRef: React.RefObject<HTMLInputElement>;
    onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onAddButtonClick: () => void;
    onDragEnd: (result: DropResult) => void;
    onOrderChange: (adId: number, newOrder: string) => void;
    onToggleAdActive: (adId: number) => void;
    onViewImage: (adId: number) => void;
    onDeleteClick: (adId: number) => void;
};

export function AdsTable({
    mainColor,
    displayType,
    onDisplayTypeChange,
    currentAds,
    fileInputRef,
    onImageUpload,
    onAddButtonClick,
    onDragEnd,
    onOrderChange,
    onToggleAdActive,
    onViewImage,
    onDeleteClick,
}: AdsTableProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card data-testid="scan-to-ads-images-card">
            <SectionTitle style={{ marginBottom: "15px" }}>
                {t("Advertising images")}
            </SectionTitle>
            <ImageSection>
                <ImageHeader>
                    <Select
                        data-testid="scan-to-ads-display-type-select"
                        value={displayType}
                        onChange={(e) => onDisplayTypeChange(e.target.value)}
                    >
                        <option value="desktop">
                            Desktop 1920*550 (png, jpg, jpeg)
                        </option>
                        <option value="mobile">
                            Mobile 750*400 (png, jpg, jpeg)
                        </option>
                    </Select>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png, image/jpeg, image/jpg"
                        style={{ display: "none" }}
                        onChange={onImageUpload}
                        data-testid="scan-to-ads-image-file-input"
                    />
                    <AddButton
                        data-testid="scan-to-ads-add-btn"
                        mainColor={mainColor}
                        onClick={onAddButtonClick}
                    >
                        {t("Add")}
                    </AddButton>
                </ImageHeader>

                <TableWrapper>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Table>
                            <thead>
                                <tr>
                                    <Th>{t("Order")}</Th>
                                    <Th>{t("Image")}</Th>
                                    <Th>{t("Image or video")}</Th>
                                    <Th style={{ textAlign: "center" }}>
                                        Actions
                                    </Th>
                                </tr>
                            </thead>
                            <Droppable droppableId="ads">
                                {(provided) => (
                                    <tbody
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {currentAds.map((ad, index) => (
                                            <Draggable
                                                key={ad.id}
                                                draggableId={String(ad.id)}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <tr
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        data-testid={`scan-to-ads-row-${ad.id}`}
                                                        style={{
                                                            backgroundColor: ad.active
                                                                ? `${mainColor}10`
                                                                : snapshot.isDragging
                                                                ? "#f8f9fa"
                                                                : "transparent",
                                                            ...provided
                                                                .draggableProps
                                                                .style,
                                                        }}
                                                    >
                                                        <Td>
                                                            <DragHandle
                                                                {...provided.dragHandleProps}
                                                                data-testid={`scan-to-ads-drag-handle-${ad.id}`}
                                                            >
                                                                <svg
                                                                    width="16"
                                                                    height="16"
                                                                    viewBox="0 0 24 24"
                                                                    fill="currentColor"
                                                                >
                                                                    <path d="M3 15h18v-2H3v2zm0 4h18v-2H3v2zm0-8h18V9H3v2zm0-6v2h18V5H3z" />
                                                                </svg>
                                                                <OrderInput
                                                                    data-testid={`scan-to-ads-order-input-${ad.id}`}
                                                                    type="number"
                                                                    value={
                                                                        ad.order
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        onOrderChange(
                                                                            ad.id,
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    min={1}
                                                                    max={
                                                                        currentAds.length
                                                                    }
                                                                    onClick={(
                                                                        e
                                                                    ) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                />
                                                            </DragHandle>
                                                        </Td>
                                                        <Td>
                                                            <ImageThumbnail>
                                                                {ad.imageUrl ||
                                                                ad.filename?.startsWith(
                                                                    "http"
                                                                ) ? (
                                                                    <img
                                                                        src={
                                                                            ad.imageUrl ||
                                                                            ad.filename
                                                                        }
                                                                        alt=""
                                                                    />
                                                                ) : (
                                                                    <svg
                                                                        width="20"
                                                                        height="20"
                                                                        viewBox="0 0 24 24"
                                                                        fill="#ccc"
                                                                    >
                                                                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                                                                    </svg>
                                                                )}
                                                            </ImageThumbnail>
                                                        </Td>
                                                        <Td>
                                                            <div
                                                                style={{
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    gap: "10px",
                                                                }}
                                                            >
                                                                <svg
                                                                    width="16"
                                                                    height="16"
                                                                    viewBox="0 0 24 24"
                                                                    fill="#666"
                                                                >
                                                                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                                                                </svg>
                                                                {ad.filename
                                                                    ?.length >
                                                                10
                                                                    ? "..." +
                                                                      ad.filename.slice(
                                                                          -10
                                                                      )
                                                                    : ad.filename}
                                                            </div>
                                                        </Td>
                                                        <Td
                                                            style={{
                                                                textAlign:
                                                                    "center",
                                                            }}
                                                        >
                                                            <ActionsCell>
                                                                <SmallToggle
                                                                    data-testid={`scan-to-ads-toggle-active-${ad.id}`}
                                                                    isActive={
                                                                        ad.active
                                                                    }
                                                                    mainColor={
                                                                        mainColor
                                                                    }
                                                                    onClick={() =>
                                                                        onToggleAdActive(
                                                                            ad.id
                                                                        )
                                                                    }
                                                                    aria-label={t(
                                                                        "Toggle advertisement visibility"
                                                                    )}
                                                                />
                                                                <ViewButton
                                                                    data-testid={`scan-to-ads-view-btn-${ad.id}`}
                                                                    onClick={() =>
                                                                        onViewImage(
                                                                            ad.id
                                                                        )
                                                                    }
                                                                    title={t(
                                                                        "View"
                                                                    )}
                                                                >
                                                                    <svg
                                                                        viewBox="0 0 24 24"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                                                                    </svg>
                                                                </ViewButton>
                                                                <DeleteButton
                                                                    data-testid={`scan-to-ads-delete-btn-${ad.id}`}
                                                                    onClick={() =>
                                                                        onDeleteClick(
                                                                            ad.id
                                                                        )
                                                                    }
                                                                    title={t(
                                                                        "Delete"
                                                                    )}
                                                                >
                                                                    <svg
                                                                        viewBox="0 0 24 24"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                                                    </svg>
                                                                </DeleteButton>
                                                            </ActionsCell>
                                                        </Td>
                                                    </tr>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                        </Table>
                    </DragDropContext>
                </TableWrapper>
            </ImageSection>
        </Card>
    );
}
